import { useEffect, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import StickyCTAs from './components/StickyCTAs';
import Home from './pages/Home';
import { Toaster } from './components/ui/sonner';

// Code-split non-home routes for faster initial paint
const About = lazy(() => import('./pages/About'));
const PersonalStyling = lazy(() => import('./pages/PersonalStyling'));
const CorporateTraining = lazy(() => import('./pages/CorporateTraining'));
const ShopTheWorld = lazy(() => import('./pages/ShopTheWorld'));
const StyleJournal = lazy(() => import('./pages/StyleJournal'));
const BookConsultation = lazy(() => import('./pages/BookConsultation'));
const ClientTransformations = lazy(() => import('./pages/ClientTransformations'));
const Contact = lazy(() => import('./pages/Contact'));
const Admin = lazy(() => import('./pages/Admin'));

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Wait for the target section to mount (lazy-loaded routes) then scroll.
      const id = hash.replace('#', '');
      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (attempts < 20) {
          attempts += 1;
          setTimeout(tryScroll, 80);
        }
      };
      tryScroll();
      return;
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname, hash]);
  return null;
}

function PageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-ivory">
      <div className="flex flex-col items-center gap-3">
        <span className="font-display text-xs tracking-[0.35em] text-champagne-deep">MONDAINE</span>
        <span className="h-px w-12 bg-champagne-deep animate-pulse" />
      </div>
    </div>
  );
}

function RevealOnScroll() {
  useEffect(() => {
    // Activate the hide+reveal animation only after hydration. The .reveal
    // class is visible-by-default in CSS until `.js-anim` is added, which
    // prevents the iOS Safari "blank Services section on first load" bug.
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
    const reducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isMobile || reducedMotion) return undefined;

    document.documentElement.classList.add('js-anim');
    const els = document.querySelectorAll('.reveal');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('in-view');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -10% 0px' });
    els.forEach((el) => io.observe(el));
    // Safety net: reveal anything still hidden after 2.5s
    const t = setTimeout(() => {
      document.querySelectorAll('.reveal:not(.in-view)').forEach((el) => el.classList.add('in-view'));
    }, 2500);
    return () => {
      io.disconnect();
      clearTimeout(t);
      document.documentElement.classList.remove('js-anim');
    };
  }, []);
  return null;
}

function App() {
  return (
    <div className="App bg-ivory text-charcoal">
      <BrowserRouter>
        <ScrollToTop />
        <RevealOnScroll />
        <Navbar />
        <main>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/personal-styling" element={<PersonalStyling />} />
              {/* Legacy URLs — client-side redirect to canonical pages */}
              <Route path="/services" element={<Navigate to="/personal-styling" replace />} />
              <Route path="/corporate-training" element={<CorporateTraining />} />
              <Route path="/retail-staff-training" element={<Navigate to="/corporate-training" replace />} />
              <Route path="/shop-the-world" element={<ShopTheWorld />} />
              <Route path="/style-journal" element={<StyleJournal />} />
              <Route path="/book-consultation" element={<BookConsultation />} />
              <Route path="/client-transformations" element={<ClientTransformations />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/admin" element={<Admin />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <StickyCTAs />
        <Toaster position="top-center" />
      </BrowserRouter>
    </div>
  );
}

export default App;
